import React, { useState, useCallback, useEffect } from "react";
import { usePlaidLink } from "react-plaid-link";
import api from "../../api";

/**
 * PlaidLinkButton - Launches Plaid Link for connecting bank accounts
 *
 * @param {string} accountId - The account ID to connect to Plaid
 * @param {function} onSuccess - Callback after successful connection
 * @param {function} onError - Callback on error
 * @param {string} buttonText - Custom button text (optional)
 * @param {string} className - Custom button classes (optional)
 */
const PlaidLinkButton = ({
  accountId,
  onSuccess,
  onError,
  buttonText = "Connect with Plaid",
  className = "",
}) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch link token when component mounts
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.post("/plaid/create-link-token");
        setLinkToken(response.data.link_token);
      } catch (err) {
        console.error("Error creating link token:", err);
        setError(err.response?.data?.detail || "Failed to initialize Plaid Link");
        if (onError) {
          onError(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLinkToken();
  }, [onError]);

  // Handle successful Plaid Link
  const handleOnSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        setLoading(true);

        // Exchange public token for access token
        const response = await api.post("/plaid/exchange-public-token", {
          public_token: publicToken,
          plaid_account_id: metadata.account_id,
          account_id: accountId,
        });

        console.log("Plaid connected successfully:", response.data);

        // Trigger sync after connection
        try {
          await api.post(`/accounts/${accountId}/sync-plaid`);
          console.log("Initial sync completed");
        } catch (syncError) {
          console.error("Initial sync failed:", syncError);
          // Don't fail the whole flow if sync fails
        }

        if (onSuccess) {
          onSuccess(response.data);
        }
      } catch (err) {
        console.error("Error exchanging public token:", err);
        setError(err.response?.data?.detail || "Failed to connect account");
        if (onError) {
          onError(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [accountId, onSuccess, onError]
  );

  // Handle Plaid Link exit
  const handleOnExit = useCallback(
    (err, metadata) => {
      if (err) {
        console.error("Plaid Link error:", err);
        setError(err.message || "Plaid Link was closed");
        if (onError) {
          onError(err);
        }
      }
    },
    [onError]
  );

  // Configure Plaid Link
  const config = {
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  };

  const { open, ready } = usePlaidLink(config);

  // Default button styling
  const defaultClassName =
    "px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  return (
    <div>
      <button
        onClick={() => open()}
        disabled={!ready || loading}
        className={className || defaultClassName}
      >
        {loading ? "Connecting..." : buttonText}
      </button>

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default PlaidLinkButton;
